import type {
  Answers,
  AxisScore,
  DimensionCode,
  DimensionMeta,
  IdeologyMatch,
  Question,
  ResultProfile,
  Tag,
  Tension,
  ValueCode,
  ValueMeta,
} from "./cvcs-types";

export const dimensionMetas: DimensionMeta[] = [
  { code: "EE", name: "Economic Equality", plainName: "equality", description: "redistribution, labor power, welfare, public provision" },
  { code: "MA", name: "Market Autonomy", plainName: "market autonomy", description: "property rights, competition, entrepreneurial reward" },
  { code: "SC", name: "State Capacity", plainName: "state capacity", description: "belief that capable public institutions can solve collective problems" },
  { code: "CL", name: "Civil Liberty", plainName: "civil liberty", description: "speech, privacy, bodily autonomy, due process, lifestyle freedom" },
  { code: "SP", name: "Social Pluralism", plainName: "pluralism", description: "comfort with diversity, minority rights, cultural openness" },
  { code: "OA", name: "Order / Authority", plainName: "order", description: "discipline, public order, norm enforcement, hierarchy" },
  { code: "NS", name: "National Solidarity / Boundary", plainName: "national boundary", description: "sovereignty, citizenship priority, border salience" },
  { code: "GC", name: "Global Cooperation", plainName: "global cooperation", description: "international law, cosmopolitan duties, transnational cooperation" },
  { code: "IC", name: "Institutional Checks", plainName: "institutional checks", description: "courts, rule of law, constitutionalism, independent oversight" },
  { code: "DD", name: "Direct Democracy / Anti-Elite", plainName: "direct democracy", description: "popular sovereignty and anti-establishment pressure" },
  { code: "ET", name: "Expert Trust / Technocracy", plainName: "expert trust", description: "deference to expertise, evidence systems, technical administration" },
  { code: "EP", name: "Environmental Precaution", plainName: "environmental precaution", description: "ecological limits, future generations, risk restraint" },
  { code: "CO", name: "Change Openness", plainName: "change openness", description: "experimentation, reform, novelty, future orientation" },
  { code: "RA", name: "Structural Attribution", plainName: "structural explanation", description: "explaining outcomes through systems and institutions" },
  { code: "HI", name: "Hierarchy Acceptance", plainName: "hierarchy acceptance", description: "comfort with unequal status, rank, stratification" },
  { code: "LO", name: "Localism / Subsidiarity", plainName: "localism", description: "local control, decentralization, community scale" },
];

export const valueMetas: ValueMeta[] = [
  { code: "CARE", name: "Care", description: "harm reduction and compassion" },
  { code: "EQUALITY", name: "Equality", description: "equal status and equal protection" },
  { code: "PROPORTION", name: "Proportion", description: "desert, reciprocity, earned reward" },
  { code: "LIBERTY", name: "Liberty", description: "non-domination and personal choice" },
  { code: "LOYALTY", name: "Loyalty", description: "obligation to family, nation, or community" },
  { code: "AUTHORITY", name: "Authority", description: "respect for legitimate order" },
  { code: "PURITY", name: "Purity", description: "sacredness, degradation, symbolic boundaries" },
  { code: "SECURITY", name: "Security", description: "safety, stability, predictability" },
  { code: "UNIVERSAL", name: "Universalism", description: "duties to strangers, humanity, future generations" },
  { code: "ACHIEVE", name: "Achievement", description: "excellence, competence, ambition" },
];

const dimensionCodes = dimensionMetas.map(d => d.code);
const valueCodes = valueMetas.map(v => v.code);
const allCodes = [...dimensionCodes, ...valueCodes] as Array<DimensionCode | ValueCode>;
const dimSet = new Set<string>(dimensionCodes);

export const likertLabels = [
  "Strongly disagree",
  "Disagree",
  "Somewhat disagree",
  "Mixed / depends",
  "Somewhat agree",
  "Agree",
  "Strongly agree",
];

export const likertEncoding: Record<number, number> = {
  1: -1,
  2: -0.67,
  3: -0.33,
  4: 0,
  5: 0.33,
  6: 0.67,
  7: 1,
};

export const importanceWeights: Record<number, number> = {
  0: 0.7,
  1: 0.85,
  2: 1,
  3: 1.15,
};

const blankAxis = <T extends string>(codes: readonly T[]): Record<T, AxisScore> =>
  Object.fromEntries(codes.map(code => [code, { score: 0, consistency: null, evidence: "low", answeredItems: 0 }])) as Record<T, AxisScore>;

type Accumulator = Record<string, { sum: number; denom: number; items: number[]; itemIds: Set<string> }>;

const makeAccumulator = (): Accumulator => Object.fromEntries(allCodes.map(code => [code, { sum: 0, denom: 0, items: [], itemIds: new Set<string>() }]));

const clamp = (value: number, min = -100, max = 100) => Math.min(max, Math.max(min, value));

export const scoreFromAccumulator = <T extends DimensionCode | ValueCode>(acc: Accumulator, codes: readonly T[]): Record<T, AxisScore> => {
  const output = {} as Record<T, AxisScore>;
  for (const code of codes) {
    const bucket = acc[code];
    if (!bucket || bucket.denom === 0) {
      output[code] = { score: 0, consistency: null, evidence: "low", answeredItems: 0 };
      continue;
    }
    const score = clamp((100 * bucket.sum) / bucket.denom);
    const mean = bucket.items.reduce((a, b) => a + b, 0) / bucket.items.length;
    const meanAbs = bucket.items.reduce((a, b) => a + Math.abs(b), 0) / bucket.items.length;
    output[code] = {
      score: Math.round(score),
      consistency: meanAbs === 0 ? 0 : Math.round((Math.abs(mean) / meanAbs) * 100) / 100,
      evidence: bucket.itemIds.size < 3 ? "low" : "ok",
      answeredItems: bucket.itemIds.size,
    };
  }
  return output;
};

const moduleImportance = (questions: Question[], answers: Answers, module: string): number => {
  const importanceQuestion = questions.find(q => q.type === "importance" && q.targetModule === module);
  const value = importanceQuestion ? answers[importanceQuestion.id] : undefined;
  return typeof value === "number" ? importanceWeights[value] ?? 1 : 1;
};

const applyTags = (acc: Accumulator, tags: Tag[], multiplier: number, importance: number, itemId: string, includeNeutralDenom = false) => {
  for (const t of tags) {
    const bucket = acc[t.code];
    if (!bucket) continue;
    const contribution = multiplier * t.weight * importance;
    const maxContribution = Math.abs(t.weight) * importance;
    bucket.sum += contribution;
    bucket.denom += maxContribution;
    bucket.items.push(contribution);
    bucket.itemIds.add(itemId);
    if (includeNeutralDenom && contribution !== 0) bucket.items.push(0);
  }
};

export const accumulateScores = (
  questions: Question[],
  answers: Answers,
  filter: (question: Question) => boolean,
): Accumulator => {
  const acc = makeAccumulator();

  for (const q of questions) {
    if (!filter(q)) continue;
    const answer = answers[q.id];
    if (answer === undefined || answer === "N") continue;

    if (q.type === "likert") {
      if (typeof answer !== "number" || q.tags.length === 0) continue;
      const encoded = likertEncoding[answer];
      if (encoded === undefined) continue;
      applyTags(acc, q.tags, encoded, moduleImportance(questions, answers, q.module), q.id);
    }

    if (q.type === "tradeoff") {
      if (typeof answer !== "number") continue;
      const x = answer / 3;
      const importance = moduleImportance(questions, answers, q.module);
      if (x < 0) applyTags(acc, q.tagsA, Math.abs(x), importance, q.id);
      if (x > 0) applyTags(acc, q.tagsB, x, importance, q.id);
      if (x === 0) applyTags(acc, [...q.tagsA, ...q.tagsB], 0, importance, q.id);
    }
  }

  return acc;
};

const isStress = (q: Question) => q.type === "tradeoff" && Boolean(q.stressor);
const isBaselineScored = (q: Question) => (q.type === "likert" || q.type === "tradeoff") && !isStress(q);

const emptyVector = (): Record<DimensionCode, number> => Object.fromEntries(dimensionCodes.map(code => [code, 0])) as Record<DimensionCode, number>;
const vector = (pairs: Partial<Record<DimensionCode, number>>): Record<DimensionCode, number> => ({ ...emptyVector(), ...pairs });

const archetypes: Array<{ name: string; note: string; vector: Record<DimensionCode, number> }> = [
  { name: "Classical Liberal / Libertarian", note: "market autonomy, civil liberty, limited state power", vector: vector({ MA: 75, CL: 80, SC: -55, EE: -45, OA: -35, IC: 45 }) },
  { name: "Social Liberal", note: "civil liberty, pluralism, public provision, institutional checks", vector: vector({ CL: 70, SP: 70, EE: 55, SC: 45, IC: 55, GC: 45, OA: -35 }) },
  { name: "Social Democrat", note: "egalitarian public capacity with pluralist institutions", vector: vector({ EE: 80, SC: 65, RA: 60, EP: 50, IC: 45, SP: 45, HI: -45 }) },
  { name: "Democratic Socialist", note: "economic equality, structural critique, popular democracy", vector: vector({ EE: 90, RA: 75, SC: 45, MA: -70, HI: -70, DD: 45 }) },
  { name: "Libertarian Socialist / Anarchist", note: "liberty and equality through local, anti-authoritarian democracy", vector: vector({ CL: 75, EE: 65, LO: 60, DD: 60, SC: -45, OA: -70, HI: -65 }) },
  { name: "Traditional Conservative", note: "order, national continuity, institutional restraint, market tolerance", vector: vector({ OA: 70, NS: 55, IC: 45, MA: 40, CO: -55, SP: -35 }) },
  { name: "National Conservative", note: "national boundary, order, anti-elite pressure, lower globalism", vector: vector({ NS: 80, OA: 65, DD: 45, GC: -65, MA: 10, EE: 10 }) },
  { name: "Christian Democrat / Communitarian Conservative", note: "social obligation, localism, order, bounded solidarity", vector: vector({ OA: 50, NS: 45, EE: 35, LO: 45, IC: 45, MA: 20 }) },
  { name: "Technocratic Centrist", note: "expert systems, institutional checks, state competence", vector: vector({ ET: 80, IC: 70, SC: 60, EE: 10, MA: 10, SP: 10, OA: 10 }) },
  { name: "Green", note: "ecological precaution, global duty, pluralism, localism", vector: vector({ EP: 90, GC: 60, SP: 50, LO: 45, EE: 45, MA: -50 }) },
  { name: "Left Populist", note: "economic equality and structural critique with anti-elite democracy", vector: vector({ EE: 75, DD: 70, RA: 70, ET: -35, NS: 5, SP: 15 }) },
  { name: "Right Populist", note: "national boundary, order, anti-elite democracy, lower expert trust", vector: vector({ NS: 75, OA: 65, DD: 70, ET: -50, GC: -65, MA: 20, EE: 5 }) },
  { name: "Liberal Internationalist", note: "global cooperation, pluralist rights, checks, expertise", vector: vector({ GC: 80, IC: 65, CL: 55, SP: 65, ET: 55, EE: 20, MA: 10 }) },
  { name: "Authoritarian Statist", note: "state capacity and order over liberties and institutional restraints", vector: vector({ SC: 70, OA: 80, NS: 55, CL: -75, IC: -55, DD: 20 }) },
];

const cosine = (a: Record<DimensionCode, number>, b: Record<DimensionCode, number>) => {
  let dot = 0;
  let magA = 0;
  let magB = 0;
  for (const code of dimensionCodes) {
    dot += a[code] * b[code];
    magA += a[code] ** 2;
    magB += b[code] ** 2;
  }
  if (magA === 0 || magB === 0) return 0;
  return dot / (Math.sqrt(magA) * Math.sqrt(magB));
};

export const matchIdeologies = (baseline: Record<DimensionCode, AxisScore>): IdeologyMatch[] => {
  const user = Object.fromEntries(dimensionCodes.map(code => [code, baseline[code].score])) as Record<DimensionCode, number>;
  return archetypes
    .map(a => {
      const similarity = cosine(user, a.vector);
      return {
        name: a.name,
        note: a.note,
        similarity,
        percentage: Math.round(Math.max(0, similarity) * 100),
      };
    })
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 5);
};

export const detectTensions = (baseline: Record<DimensionCode, AxisScore>): Tension[] => {
  const s = (code: DimensionCode) => baseline[code].score;
  const tensions: Tension[] = [];
  const high = (code: DimensionCode) => s(code) >= 35;
  const low = (code: DimensionCode) => s(code) <= -25;
  if (high("CL") && high("OA")) tensions.push({ title: "Liberty-order tension", body: "You protect personal freedoms while also seeing order and enforcement as real civic goods." });
  if (high("EE") && high("MA")) tensions.push({ title: "Egalitarian-market tension", body: "You combine concern for economic equality with respect for markets, entrepreneurship, or earned reward." });
  if (high("DD") && high("IC")) tensions.push({ title: "Popular sovereignty vs institutional checks", body: "You want public control, but you also value courts, audits, procedures, and constraints on raw majorities." });
  if (high("GC") && high("NS")) tensions.push({ title: "Cosmopolitan patriot", body: "You hold duties beyond borders alongside a meaningful sense of national solidarity or membership." });
  if (high("EP") && high("MA")) tensions.push({ title: "Green innovation / market environmentalism", body: "You take ecological risk seriously without rejecting market-led experimentation." });
  if (high("EE") && low("SC")) tensions.push({ title: "Anti-statist egalitarian", body: "You lean egalitarian, but your preferred route may be cooperative, local, or non-bureaucratic rather than state-centered." });
  if (high("ET") && high("DD")) tensions.push({ title: "Expert trust plus democratic pressure", body: "You listen to technical expertise while also wanting stronger popular checks on elites." });
  if (high("SP") && high("OA")) tensions.push({ title: "Pluralism plus order concern", body: "You are open to diversity, but not indifferent to shared norms, stability, or disorder." });
  return tensions;
};

const delta = (stress: number, base: number) => Math.round(stress - base);

const getStressDeltas = (
  baseline: Record<DimensionCode, AxisScore>,
  stress: Record<DimensionCode, AxisScore>,
  values: Record<ValueCode, AxisScore>,
  stressValues: Record<ValueCode, AxisScore>,
): Partial<Record<DimensionCode | ValueCode, number>> => {
  const out: Partial<Record<DimensionCode | ValueCode, number>> = {};
  for (const code of dimensionCodes) out[code] = delta(stress[code].score, baseline[code].score);
  for (const code of valueCodes) out[code] = delta(stressValues[code].score, values[code].score);
  return out;
};

const avg = (...values: number[]) => Math.round(values.reduce((a, b) => a + b, 0) / values.length);

const groupedStress = (d: Partial<Record<DimensionCode | ValueCode, number>>) => ({
  "Threat Shift": avg(d.OA ?? 0, d.SECURITY ?? 0, -(d.CL ?? 0)),
  "Scarcity Shift": avg(d.EE ?? 0, d.SC ?? 0, -(d.MA ?? 0)),
  "Corruption Shift": avg(d.DD ?? 0, -(d.IC ?? 0), -(d.ET ?? 0)),
  "Cultural Conflict Shift": avg(d.OA ?? 0, d.NS ?? 0, -(d.SP ?? 0), -(d.CL ?? 0)),
  "Complexity Shift": avg(d.ET ?? 0, d.IC ?? 0, -(d.DD ?? 0)),
});

export const scoreQuestionnaire = (questions: Question[], answers: Answers): ResultProfile => {
  const baselineAcc = accumulateScores(questions, answers, isBaselineScored);
  const stressAcc = accumulateScores(questions, answers, isStress);
  const baseline = scoreFromAccumulator(baselineAcc, dimensionCodes);
  const stress = scoreFromAccumulator(stressAcc, dimensionCodes);
  const values = scoreFromAccumulator(baselineAcc, valueCodes);
  const stressValueScores = scoreFromAccumulator(stressAcc, valueCodes);
  const stressDeltas = getStressDeltas(baseline, stress, values, stressValueScores);
  const topValues = valueMetas
    .map(v => ({ code: v.code, name: v.name, score: values[v.code].score }))
    .filter(v => v.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  return {
    baseline,
    stress,
    values,
    stressValueScores,
    stressDeltas,
    groupedStress: groupedStress(stressDeltas),
    ideologyMatches: matchIdeologies(baseline),
    tensions: detectTensions(baseline),
    topValues,
  };
};

const isScoredQuestion = (q: Question) =>
  (q.type === "likert" && q.tags.length > 0) ||
  (q.type === "tradeoff" && (q.tagsA.length > 0 || q.tagsB.length > 0));

export const answeredCount = (questions: Question[], answers: Answers) =>
  questions.filter(q => isScoredQuestion(q) && answers[q.id] !== undefined).length;

export const scoredQuestionCount = (questions: Question[]) => questions.filter(isScoredQuestion).length;

export const isDimensionCode = (code: string): code is DimensionCode => dimSet.has(code);
